import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';

interface RiskAssessment {
  workshop_id: number;
  workshop_number: string;
  title: string;
  risk_score: number;
  risk_factors: Array<{
    factor: string;
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }>;
  success_probability: number;
  recommended_actions: string[];
}

interface RiskAssessmentGaugeProps {
  riskAssessments: RiskAssessment[];
  title?: string;
}

const RiskAssessmentGauge: React.FC<RiskAssessmentGaugeProps> = ({ 
  riskAssessments, 
  title = "Risk Assessment" 
}) => {
  const getRiskLevel = (score: number) => {
    if (score >= 80) return { level: 'Critical', color: 'red', icon: XCircle };
    if (score >= 60) return { level: 'High', color: 'orange', icon: AlertTriangle };
    if (score >= 40) return { level: 'Medium', color: 'yellow', icon: Info };
    return { level: 'Low', color: 'green', icon: CheckCircle };
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const highRiskWorkshops = riskAssessments.filter(r => r.risk_score >= 70);
  const mediumRiskWorkshops = riskAssessments.filter(r => r.risk_score >= 40 && r.risk_score < 70);
  const lowRiskWorkshops = riskAssessments.filter(r => r.risk_score < 40);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          <span className="text-sm text-gray-600">Risk Analysis</span>
        </div>
      </div>

      {/* Risk Distribution Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="font-semibold text-red-700">High Risk</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{highRiskWorkshops.length}</p>
          <p className="text-sm text-red-600">Workshops</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <span className="font-semibold text-yellow-700">Medium Risk</span>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{mediumRiskWorkshops.length}</p>
          <p className="text-sm text-yellow-600">Workshops</p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="font-semibold text-green-700">Low Risk</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{lowRiskWorkshops.length}</p>
          <p className="text-sm text-green-600">Workshops</p>
        </div>
      </div>

      {/* High Risk Workshops List */}
      {highRiskWorkshops.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-500" />
            High Risk Workshops (Immediate Attention Required)
          </h4>
          <div className="space-y-3">
            {highRiskWorkshops.slice(0, 5).map((workshop) => {
              const riskLevel = getRiskLevel(workshop.risk_score);
              const IconComponent = riskLevel.icon;
              
              return (
                <div key={workshop.workshop_id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="font-semibold text-gray-900">{workshop.workshop_number}</h5>
                      <p className="text-sm text-gray-600">{workshop.title}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <IconComponent className={`w-4 h-4 text-${riskLevel.color}-500`} />
                      <span className={`text-sm font-semibold text-${riskLevel.color}-600`}>
                        {workshop.risk_score.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${workshop.risk_score}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Success Probability: {workshop.success_probability.toFixed(0)}%
                    </p>
                  </div>

                  {workshop.risk_factors.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Risk Factors:</p>
                      <div className="space-y-1">
                        {workshop.risk_factors.slice(0, 2).map((factor, index) => (
                          <div key={index} className={`px-2 py-1 rounded text-xs border ${getSeverityColor(factor.severity)}`}>
                            <span className="font-medium">{factor.factor}:</span> {factor.description}
                          </div>
                        ))}
                        {workshop.risk_factors.length > 2 && (
                          <p className="text-xs text-gray-500">
                            +{workshop.risk_factors.length - 2} more factors
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {workshop.recommended_actions.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Recommended Actions:</p>
                      <ul className="space-y-1">
                        {workshop.recommended_actions.slice(0, 2).map((action, index) => (
                          <li key={index} className="text-xs text-gray-600 flex items-start gap-1">
                            <span className="text-blue-500 mt-0.5">•</span>
                            {action}
                          </li>
                        ))}
                        {workshop.recommended_actions.length > 2 && (
                          <li className="text-xs text-gray-500">
                            +{workshop.recommended_actions.length - 2} more actions
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h5 className="font-semibold text-gray-900 mb-2">Overall Risk Distribution</h5>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">High Risk</span>
              <span className="text-sm font-semibold text-red-600">
                {((highRiskWorkshops.length / riskAssessments.length) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Medium Risk</span>
              <span className="text-sm font-semibold text-yellow-600">
                {((mediumRiskWorkshops.length / riskAssessments.length) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Low Risk</span>
              <span className="text-sm font-semibold text-green-600">
                {((lowRiskWorkshops.length / riskAssessments.length) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h5 className="font-semibold text-gray-900 mb-2">Success Probability</h5>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Average</span>
              <span className="text-sm font-semibold text-blue-600">
                {(riskAssessments.reduce((sum, w) => sum + w.success_probability, 0) / riskAssessments.length).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Highest</span>
              <span className="text-sm font-semibold text-green-600">
                {Math.max(...riskAssessments.map(w => w.success_probability)).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Lowest</span>
              <span className="text-sm font-semibold text-red-600">
                {Math.min(...riskAssessments.map(w => w.success_probability)).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAssessmentGauge;


